<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="modalHeight"
      @on-base-modal-close="cancelModal"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="m-title">
          {{ getMessageText(messages.DISP_EL_FIELD_LIST) }}
        </div>
      </template>

      <element-description
        v-if="isDescriptionReady"
        :label="descriptionText"
        :outer-container-class="'m-items'"
        :elem-description="desc"
        :value-container-class="'mt8 obj-select'"
        :focus="true"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <div class="m-items">
        <label>{{ getMessageText(messages.FIELD_LIST_STYLE_LABEL) }}:</label>
        <search-box-simple
          ref="searchBox"
          :list="styleType"
          :value="selectedItem"
          @on-search-box-item-selected="onItemSelected"
        />
      </div>

      <div class="m-table_columns">
        <div ref="listItems" class="m-list_items">
          <draggable-item
            v-for="(item, index) in rows"
            :key="item.id"
            :info="item"
            @on-draggable-drop="onDraggableDrop"
            @on-draggable-drag-start="onDraggableStart"
          >
            <template>
              <div class="field-wrapper">
                <field-list-element
                  :id="item.id"
                  :should-focus="shouldFocusIcon(index)"
                  :column-data="item"
                  @on-delete-column="onDeleteColumn"
                  @on-selected-field-column="onSelectedFieldColumn"
                />
              </div>
            </template>
          </draggable-item>
        </div>

        <div class="m-add_column">
          <button
            ref="addColumnBtn"
            class="m-button neutral"
            @click="addColumn"
            :disabled="isAddColumnDisabled"
          >
            {{ replaceMessageText(messages.ADD_ITEM, messages.FIELD) }}
          </button>
        </div>
      </div>

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" @click="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>

      <div ref="deleteColumnModal">
        <simple-modal
          ref="simpleModal"
          :show="displaySimpleModal"
          :message="getMessageText(messages.ARE_YOU_SURE)"
          :cancel-text="getMessageText(messages.CANCEL)"
          :ok-text="getMessageText(messages.OK)"
          @on-simple-modal-cancel="onCancelDeleteColumn"
          @on-simple-modal-ok="onConfirmDeleteColumn"
        />
      </div>
    </base-modal>
  </section>
</template>

<script lang="js">
  import { v4 as uuidv4 } from "uuid";
  import sortBy from "lodash/sortBy";
  import store from "@/store";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import DraggableItem from "@/views/drag-drop-pages/components/list/DraggableItem";
  import FieldListElement from "@/views/drag-drop-pages/components/builder/modal/FieldListElement";
  import ElementDescription from "@/components/ElementDescription";
  import utils from "@/utils";

  export default  {
    name: "add-field-list-modal",
    components: {
      BaseModal,
      SimpleModal,
      SearchBoxSimple,
      DraggableItem,
      FieldListElement,
      ElementDescription
    },
    props: {
      id: {
        type: String
      },
      editData: {
        type: Object
      }
    },
    mounted () {
      if(this.editData) {
        this.setupEditModalData(this.editData);
      }
      this.isDescriptionReady = true;
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      isSubmitDisabled() {
        return this.selectedItem === null || this.isNotValidFieldList;
      },
      isAddColumnDisabled() {
        return this.selectedItem === null;
      },
      styleType() {
        return [
          {
            id: 1,
            name: "above",
            label: this.getMessageText(this.messages.FIELD_LIST_STYLE_ABOVE)
          },
          {
            id: 2,
            name: "inline",
            label: this.getMessageText(this.messages.FIELD_LIST_STYLE_INLINE)
          },
          {
            id: 3,
            name: "horizontal",
            label: this.getMessageText(this.messages.FIELD_LIST_STYLE_HORIZONTAL)
          }
        ];
      },
      desc() {
        return this.editData ? this.editData.props.description : "";
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    data () {
      return {
        modalHeight: 500,
        displaySimpleModal: false,
        selectedItem: null,
        isNotValidFieldList: true,
        fields: {},
        rows: [],
        elementDescription: null,
        isDescriptionReady: false
      }
    },
    methods: {
      setupEditModalData(editData) {
        this.onDeDescriptionChanged(editData.props.description);
        this.selectedItem = this.styleType.find(e => e.name === editData.props.style);
        for(const item of editData.props.items) {
          const row = this.addColumn();
          row.label = item.label;
          row.value = item.value;
          row.valueAction = item.valueAction;
          row.icon = item.icon;
          this.fields[row.id] = item;
        }
        this.isNotValidFieldList = false;
      },
      cancelModal() {
        const EVENT = "on-add-modal-close";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, {id: this.id});
        }
      },
      createBtn() {
        const fields = [];
        for(const row of this.rows) {
          const col = this.fields[row.id];
          if(col) {
            fields.push(col);
          }
        }
        const obj = {
          modal: {
            id: this.id,
            create: "FieldList",
            editElementId: this.editData? this.editData.id: null
          },
          props: {
            description: this.elementDescription,
            data: {
              style: this.selectedItem.name,
              items: fields
            }
          }
        };
        const EVENT = "on-add-modal-done";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          dragAndDrop.makeAllComponentsDraggable();
          this.$emit(EVENT, obj);
        }
      },
      onItemSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.selectedItem = null;
        } else {
          this.selectedItem = ctx;
        }
      },
      shouldFocusIcon(index) {
        return index === this.rows.length - 1 && this.isNotValidFieldList;
      },
      addColumn() {
        const id = "field-" + uuidv4();
        const row = {
          id: id,
          rowId: this.rows.length
        };
        this.rows.push(row);
        this.isNotValidFieldList = true;
        return row;
      },
      onDeDescriptionChanged(input) {
        this.elementDescription = input;
      },
      onDraggableStart(ev) {
        this.from = ev.data.info;
      },
      onCancelDeleteColumn() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteColumn() {
        const filtered = this.rows.filter(item => item.id !== this.deleteItem.id);
        this.rows = filtered;
        this.displaySimpleModal = false;
        delete this.fields[this.deleteItem.id];
        this.validateFields();
      },
      onSelectedFieldColumn(obj) {
        this.fields[obj.id] = obj.data;
        this.validateFields();
      },
      validateFields() {
        this.isNotValidFieldList = false;
        for(const [, value] of Object.entries(this.fields)) {
          if(!value.label || !value.value || !utils.isValidAction(value.valueAction)) {
            this.isNotValidFieldList = true;
            break;
          }
        }
      },
      onDeleteColumn(obj) {
        this.deleteItem = obj;
        this.displaySimpleModal = true;
      },
      onDraggableDrop(ev) {
        const from = this.from;
        const to = ev.data.info;
        if(from.rowId !== to.rowId) {
          if(to.rowId < from.rowId) {
            this.moveDown(from, to);
          } else {
            this.moveUp(from, to);
          }
          this.rows = sortBy(this.rows, "rowId", "asc");
        }
      },
      moveDown(from, to) {
        let tmpRowId = 0;
        for(const row of this.rows) {
          if(row.id === to.id) {
            from.rowId = tmpRowId++;
            row.rowId = tmpRowId++;
          } else if(row.id !== from.id) {
            row.rowId = tmpRowId++;
          }
        }
      },
      moveUp(from, to) {
        const length = this.rows.length;
        let tmpRowId = length - 1;
        for(let i = length - 1; i > -1; i--) {
          const row = this.rows[i];
          if(row.id === to.id) {
            from.rowId = tmpRowId--;
            row.rowId = tmpRowId--;
          } else if(row.id !== from.id) {
            row.rowId = tmpRowId--;
          }
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .m-items {
    grid-template-columns: 6rem auto;
    margin-bottom: 10px;
  }
  .m-title {
    text-align: left;
  }
  .m-table_columns {
    display: flex;
    flex-direction: column;
    label {
      margin-bottom: 4px;
    }
  }
  .m-add_column {
    margin-top: 10px;
  }
  .field-wrapper {
    margin-bottom: 0.5rem;
  }
</style>
